/**
 * Patch Serialization Utilities
 * 
 * Pure transformation functions for patch data serialization/deserialization.
 * No state, no DOM — just data in, formatted data out.
 * 
 * This is the single place to swap JSON → binary format later.
 */

import { generateId } from './index.js';

// Current patch format version
export const PATCH_VERSION = 3;

/**
 * Create a serializable patch data structure
 * 
 * @param {Object} options
 * @param {Array} options.templates - Device definitions
 * @param {Array} options.devices - Device instances
 * @param {Array} options.connections - Cable connections
 * @param {Object} [options.settings] - Patch-specific settings (name, cableStyle, etc.)
 * @returns {Object} Patch data object ready for serialization
 */
export function createPatchData({ templates, devices, connections, settings }) {
    // DEFENSIVE: Deep clone templates to ensure all properties are preserved
    const clonedTemplates = (templates || []).map(t => {
        const cloned = { ...t };
        // DEFENSIVE: Explicitly preserve isRouter (even if false/undefined)
        if ('isRouter' in t) {
            cloned.isRouter = t.isRouter;
        }
        return cloned;
    });
    
    // [ISROUTER] Log templates with isRouter for tracing
    const routerTemplates = clonedTemplates.filter(t => t.isRouter);
    if (routerTemplates.length > 0) {
        console.log('[ISROUTER] createPatchData: Creating patch with router templates:', 
            routerTemplates.map(t => `"${t.name}" isRouter=${t.isRouter}`).join(', '));
    }
    
    const data = {
        version: PATCH_VERSION,
        templates: clonedTemplates,
        devices: devices || [],
        connections: connections || []
    };
    
    // Only include settings if provided and non-empty
    if (settings && Object.keys(settings).length > 0) {
        data.settings = settings;
    }
    
    return data;
}

/**
 * Validate patch data format and version
 * 
 * @param {Object} patchData - Patch data to validate
 * @returns {{ valid: boolean, error?: string, needsMigration?: boolean }}
 */
export function validatePatchData(patchData) {
    if (!patchData) {
        return { valid: false, error: 'No patch data provided' };
    }
    
    if (typeof patchData.version !== 'number') {
        return { valid: false, error: 'Missing or invalid version field' };
    }
    
    // v3+ requires templates
    if (patchData.version >= 3) {
        if (!Array.isArray(patchData.templates)) {
            return { valid: false, error: 'Missing templates array (required for v3+)' };
        }
        if (!Array.isArray(patchData.devices)) {
            return { valid: false, error: 'Missing devices array' };
        }
    }
    
    // v2 and below: check for migration
    if (patchData.version < 3) {
        if (!Array.isArray(patchData.devices) || patchData.devices.length === 0) {
            return { valid: false, error: 'Invalid v2 patch: missing devices' };
        }
        return { valid: true, needsMigration: true };
    }
    
    return { valid: true };
}

/**
 * Check if patch data needs migration to current version
 * 
 * @param {Object} patchData 
 * @returns {boolean}
 */
export function needsMigration(patchData) {
    return patchData && typeof patchData.version === 'number' && patchData.version < PATCH_VERSION;
}

/**
 * Remap all IDs in patch data for merge operations
 * Creates new IDs for templates, devices, and connections, and updates all references.
 * 
 * @param {Object} patchData - Original patch data
 * @returns {{ 
 *   patchData: Object, 
 *   templateIdMap: Map<string, string>, 
 *   deviceIdMap: Map<string, string> 
 * }}
 */
export function remapPatchIds(patchData) {
    const templateIdMap = new Map();
    const deviceIdMap = new Map();
    
    // Remap templates - DEFENSIVE: explicitly preserve isRouter
    const templates = (patchData.templates || []).map(t => {
        const newId = generateId();
        templateIdMap.set(t.id, newId);
        const remapped = { ...t, id: newId };
        // DEFENSIVE: Explicitly preserve isRouter
        if ('isRouter' in t) {
            remapped.isRouter = t.isRouter;
            if (t.isRouter) {
                console.log(`[ISROUTER] remapPatchIds: template "${t.name}" isRouter=${t.isRouter} (preserved)`);
            }
        }
        return remapped;
    });
    
    // Remap devices (with template reference update)
    const devices = (patchData.devices || []).map(d => {
        const newId = generateId();
        deviceIdMap.set(d.id, newId);
        return {
            ...d,
            id: newId,
            templateId: templateIdMap.get(d.templateId) || d.templateId
        };
    });
    
    // Remap connections (with device reference updates)
    const connections = (patchData.connections || []).map(c => ({
        ...c,
        id: generateId(),
        sourceDeviceId: deviceIdMap.get(c.sourceDeviceId) || c.sourceDeviceId,
        targetDeviceId: deviceIdMap.get(c.targetDeviceId) || c.targetDeviceId
    }));
    
    const remappedData = {
        version: patchData.version,
        templates,
        devices,
        connections
    };
    
    // Preserve settings if present
    if (patchData.settings) {
        remappedData.settings = { ...patchData.settings };
    }
    
    return {
        patchData: remappedData,
        templateIdMap,
        deviceIdMap
    };
}

/**
 * Extract a subset of patch data for the given device IDs
 * Includes only the templates and connections relevant to those devices.
 * 
 * @param {Object} options
 * @param {Set<string>|Array<string>} options.deviceIds - IDs of devices to extract
 * @param {Array} options.allTemplates - All available templates
 * @param {Array} options.allDevices - All available devices
 * @param {Array} options.allConnections - All available connections
 * @param {boolean} [options.internalConnectionsOnly=true] - Only include connections between selected devices
 * @returns {Object} Patch data containing only the selected subset
 */
export function extractSubset({ 
    deviceIds, 
    allTemplates, 
    allDevices, 
    allConnections,
    internalConnectionsOnly = true 
}) {
    const deviceIdSet = deviceIds instanceof Set ? deviceIds : new Set(deviceIds);
    
    // Get devices in selection
    const devices = allDevices.filter(d => deviceIdSet.has(d.id));
    
    // Get templates for selected devices
    const templateIds = new Set(devices.map(d => d.templateId));
    const templates = allTemplates.filter(t => templateIds.has(t.id));
    
    // Get connections
    let connections;
    if (internalConnectionsOnly) {
        // Only connections where both endpoints are in selection
        connections = allConnections.filter(c =>
            deviceIdSet.has(c.sourceDeviceId) && deviceIdSet.has(c.targetDeviceId)
        );
    } else {
        // Any connection touching a selected device
        connections = allConnections.filter(c =>
            deviceIdSet.has(c.sourceDeviceId) || deviceIdSet.has(c.targetDeviceId)
        );
    }
    
    return createPatchData({ templates, devices, connections });
}

/**
 * Deep clone patch data
 * Use before mutations to ensure immutability.
 * 
 * @param {Object} patchData 
 * @returns {Object} Deep cloned copy
 */
export function clonePatchData(patchData) {
    return JSON.parse(JSON.stringify(patchData));
}

/**
 * Get summary statistics for patch data
 * 
 * @param {Object} patchData 
 * @returns {{ templateCount: number, deviceCount: number, connectionCount: number }}
 */
export function getPatchStats(patchData) {
    return {
        templateCount: patchData?.templates?.length || 0,
        deviceCount: patchData?.devices?.length || 0,
        connectionCount: patchData?.connections?.length || 0
    };
}

// Re-export binary utilities for convenience
// These handle the actual encoding/decoding to binary/share-string format
export { 
    toShareString, 
    fromShareString,
    encodeForStorage,
    decodeFromStorage,
    compress,
    decompress,
    crc32,
    bytesToBase64,
    base64ToBytes,
    sha256,
    computeContentHash
} from './binary.js';
